import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchAuthSession } from '../util/amplifyAuth';
import { createCheckoutSession, getSubscriptionStatus, PLAN_CATALOG } from '../components/API';
import UserHeader from '../components/UserHeader/UserHeader';
import '../styles/Pricing.css';

// UI presentation metadata, keyed by plan. Price IDs / amounts come from the
// shared catalog (src/util/plans.js) so they're never duplicated here.
const PLAN_UI = {
    monthly: {
        label: 'Monthly',
        note: 'Billed monthly',
        features: [
            'Health assistant access',
            'Lab result processing & insights',
            'Organ system health scoring',
            'Health trend tracking',
        ],
    },
    annual: {
        label: 'Annual',
        note: '~$16.58/mo billed annually',
        badge: `Save ${Math.round((1 - 199 / (20 * 12)) * 100)}%`,
        highlighted: true,
        features: [
            'Health assistant access',
            'Lab result processing & insights',
            'Organ system health scoring',
            'Health trend tracking',
            'Personalized supplement recommendations',
        ],
    },
    elite: {
        label: 'Elite',
        note: 'Billed annually',
        tag: 'Full Service',
        features: [
            'Everything in Annual',
            'Initial comprehensive Elite lab panel',
            'Repeat lab panels re-tests per year as recommended',
            'Recommended supplements + shipping included',
            'Protocol and analysis reviews with a partner physician (2x)',
            'Priority processing & insights',
            'Priority customer support',
        ],
    },
};

const PLANS = PLAN_CATALOG.map(p => ({ ...p, ...PLAN_UI[p.key] }));

const PAGE_CONFIG = {
    patient: {
        title: 'Health Analysis',
        subtitle: 'Personalized lab analysis, health reports, and guided wellness recommendations.',
    },
    physician: {
        title: 'Provider Analysis',
        subtitle: 'Patient data analysis, protocol recommendations, and health report generation.',
    },
};

function Pricing() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loadingKey, setLoadingKey] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [subscriptionActive, setSubscriptionActive] = useState(false);
    const [isDoctor, setIsDoctor] = useState(false);
    const [ready, setReady] = useState(false);
    const autoPlanKey = searchParams.get('auto');

    useEffect(() => {
        fetchAuthSession()
            .then(async session => {
                const token = session.tokens?.accessToken?.toString();
                if (token) {
                    setIsLoggedIn(true);
                    const groups = session.tokens?.idToken?.payload['cognito:groups'] || [];
                    setIsDoctor(groups.includes('Doctors'));
                    // Always check live status before declaring ready — the cached ID
                    // token's groups are stale right after a purchase, and the ?auto=
                    // checkout effect must not race ahead of this and start a
                    // duplicate subscription.
                    try {
                        const res = await getSubscriptionStatus();
                        const s = res.subscription?.status;
                        if (s === 'active' || s === 'trialing') setSubscriptionActive(true);
                    } catch { /* treat as no subscription */ }
                }
                setReady(true);
            })
            .catch(() => setReady(true));
    }, []);

    useEffect(() => {
        if (!ready || !isLoggedIn || subscriptionActive || !autoPlanKey) return;
        const plan = PLANS.find(p => p.key === autoPlanKey);
        if (!plan) return;
        setLoadingKey(plan.key);
        window.history.replaceState(null, '', '/pricing');
        createCheckoutSession('subscription', { priceId: plan.priceId })
            .then(res => { window.location.href = res.checkoutUrl; })
            .catch(err => {
                if (err?.status === 409) setSubscriptionActive(true);
                setLoadingKey(null);
            });
    }, [ready, isLoggedIn, subscriptionActive, autoPlanKey]);

    const handleCheckout = async (plan) => {
        if (!isLoggedIn) {
            navigate('/signup?redirect=' + encodeURIComponent('/pricing?auto=' + plan.key));
            return;
        }
        setLoadingKey(plan.key);
        try {
            const res = await createCheckoutSession('subscription', { priceId: plan.priceId });
            window.location.href = res.checkoutUrl;
        } catch (err) {
            console.error('Checkout error:', err);
            // 409 = already subscribed — flip to the active card instead of the grid
            if (err?.status === 409) setSubscriptionActive(true);
            setLoadingKey(null);
        }
    };

    if (!ready) return null;

    const config = isDoctor ? PAGE_CONFIG.physician : PAGE_CONFIG.patient;
    const backPath = isDoctor ? '/doctor' : '/home';

    return (
        <div className="pricing-container">
            <UserHeader />
            <div className="pricing-content">
                <button className="pricing-back" onClick={() => navigate(backPath)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5" />
                        <path d="M12 19l-7-7 7-7" />
                    </svg>
                    {isDoctor ? 'Back to Dashboard' : 'Back to Home'}
                </button>

                <h1 className="pricing-title">{config.title}</h1>
                <p className="pricing-subtitle">{config.subtitle}</p>
                <p className="pricing-disclaimer">Not a substitute for professional medical advice, diagnosis, or treatment.</p>

                {subscriptionActive ? (
                    <div className="pricing-active">
                        <div className="pricing-active-badge">Active</div>
                        <p>You have an active subscription.</p>
                        <button className="pricing-manage-btn" onClick={() => navigate('/settings?section=subscription')}>
                            Manage Subscription
                        </button>
                    </div>
                ) : (
                    <div className="pricing-grid">
                        {PLANS.map(plan => (
                            <div
                                key={plan.key}
                                className={`pricing-card${plan.highlighted ? ' pricing-card-highlighted' : ''}${plan.key === 'elite' ? ' pricing-card-elite' : ''}`}
                            >
                                {plan.badge && <div className="pricing-save-badge">{plan.badge}</div>}
                                {plan.tag && <div className="pricing-elite-badge">{plan.tag}</div>}

                                <div className="pricing-card-header">
                                    <span className="pricing-plan-label">{plan.label}</span>
                                </div>

                                <div className="pricing-price">
                                    <span className="pricing-amount">{plan.amount}</span>
                                    <span className="pricing-interval">{plan.interval}</span>
                                </div>
                                <p className="pricing-annual-note">{plan.note}</p>

                                <ul className="pricing-features">
                                    {plan.features.map(f => <li key={f}>{f}</li>)}
                                </ul>

                                <button
                                    className="pricing-cta"
                                    onClick={() => handleCheckout(plan)}
                                    disabled={loadingKey !== null}
                                >
                                    {loadingKey === plan.key ? 'Redirecting...' : 'Get Started'}
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <p className="pricing-fine-print">
                    Cancel anytime — access continues through the end of your billing period. Tax calculated at checkout.
                    {' '}<a href="/legal/refund-policy" className="pricing-fine-link">Refund policy</a>
                </p>
            </div>
        </div>
    );
}

export default Pricing;
